import { motion } from 'framer-motion';
import {
  Activity,
  AlertCircle,
  CheckCircle,
  GitBranch,
  RefreshCw,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatExerciseTitle } from '../utils/formatTitle';
import { getApiUrl } from '../utils/apiUrl';

interface ExerciseVersion {
  id: string;
  version: string;
  github_commit_hash: string;
  github_branch: string;
  content: any;
  metadata: any;
  is_active: boolean;
  created_at: string;
}

interface Exercise {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
}

interface ExerciseWithVersions extends Exercise {
  versions: ExerciseVersion[];
  activeVersion?: ExerciseVersion;
}

const ExerciseVersionManager: React.FC = () => {
  const [exercises, setExercises] = useState<ExerciseWithVersions[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const baseUrl = getApiUrl().replace(/\/api\/?$/, '');

      // Get all exercises
      const exercisesResponse = await fetch(`${baseUrl}/api/exercises`);
      const exercisesData = await exercisesResponse.json();

      // Get versions for each exercise
      const exercisesWithVersions = await Promise.all(
        (exercisesData.exercises || []).map(async (exercise: Exercise) => {
          const versionsResponse = await fetch(
            `${baseUrl}/api/exercise-versions/${exercise.id}/versions`
          );
          const versionsData = await versionsResponse.json();

          const activeVersion = versionsData.versions.find(
            (v: ExerciseVersion) => v.is_active
          );

          return {
            ...exercise,
            versions: versionsData.versions,
            activeVersion,
          };
        })
      );

      setExercises(exercisesWithVersions);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncExercise = async (exerciseId: string) => {
    try {
      setSyncing(exerciseId);
      const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
      const response = await fetch(
        `${baseUrl}/api/exercise-versions/${exerciseId}/sync`,
        {
          method: 'POST',
        }
      );

      if (response.ok) {
        await fetchExercises(); // Refresh data
      } else {
        console.error('Sync failed');
      }
    } catch (error) {
      console.error('Error syncing exercise:', error);
    } finally {
      setSyncing(null);
    }
  };

  const syncAllExercises = async () => {
    try {
      setSyncing('all');
      const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
      const response = await fetch(
        `${baseUrl}/api/exercise-versions/sync-all`,
        {
          method: 'POST',
        }
      );

      if (response.ok) {
        await fetchExercises(); // Refresh data
      } else {
        console.error('Sync all failed');
      }
    } catch (error) {
      console.error('Error syncing all exercises:', error);
    } finally {
      setSyncing(null);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='w-16 h-16 mx-auto mb-4'>
            <div className='hexagon bg-gradient-to-r from-hexagon to-hexagon-dark animate-pulse'></div>
          </div>
          <p className='text-gray-600'>Loading exercise versions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className='flex items-center justify-between'
      >
        <div>
          <div className='flex items-center space-x-4 mb-2'>
            <h1 className='text-3xl font-bold text-gray-900'>
              Exercise Version Manager
            </h1>
            <Link
              to='/admin/sync'
              className='text-sm text-hexagon hover:text-hexagon-dark font-medium flex items-center space-x-1'
            >
              <Activity className='w-4 h-4' />
              <span>View Sync Dashboard</span>
            </Link>
          </div>
          <p className='text-gray-600 mt-2'>
            Manage exercise versions and activate specific versions
          </p>
        </div>

        <div className='flex items-center space-x-4'>
          <button
            onClick={syncAllExercises}
            disabled={syncing === 'all'}
            className='inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50'
          >
            <RefreshCw
              className={`w-4 h-4 ${syncing === 'all' ? 'animate-spin' : ''}`}
            />
            <span>{syncing === 'all' ? 'Syncing...' : 'Sync All'}</span>
          </button>
        </div>
      </motion.div>

      {/* Exercises List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className='grid grid-cols-1 lg:grid-cols-2 gap-6'
      >
        {exercises.map(exercise => (
          <div
            key={exercise.id}
            className='glass-effect rounded-2xl p-6 hover:shadow-lg transition-shadow duration-200'
          >
            <div className='flex items-start justify-between mb-4'>
              <div className='flex-1'>
                <h3 className='text-xl font-semibold text-gray-900 mb-2'>
                  {formatExerciseTitle(exercise.title)}
                </h3>
                <p className='text-gray-600 text-sm mb-3'>
                  {exercise.description}
                </p>

                <div className='flex items-center gap-3'>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(
                      exercise.difficulty
                    )}`}
                  >
                    {exercise.difficulty}
                  </span>
                  <span className='text-xs text-gray-500'>
                    {exercise.category}
                  </span>
                </div>
              </div>

              <button
                onClick={() => syncExercise(exercise.id)}
                disabled={syncing === exercise.id}
                className='inline-flex items-center space-x-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50'
              >
                <RefreshCw
                  className={`w-3 h-3 ${
                    syncing === exercise.id ? 'animate-spin' : ''
                  }`}
                />
                <span className='text-xs'>Sync</span>
              </button>
            </div>

            {/* Version Info */}
            <div className='border-t pt-4'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium text-gray-700'>
                  Versions
                </span>
                <span className='text-xs text-gray-500'>
                  {exercise.versions.length} version
                  {exercise.versions.length !== 1 ? 's' : ''}
                </span>
              </div>

              {exercise.activeVersion && (
                <div className='flex items-center space-x-2 mb-2'>
                  <CheckCircle className='w-4 h-4 text-green-500' />
                  <span className='text-sm text-gray-700'>
                    Active: {exercise.activeVersion.version}
                  </span>
                </div>
              )}

              {exercise.versions.length > 0 && (
                <div className='space-y-1'>
                  {exercise.versions.slice(0, 3).map(version => (
                    <div
                      key={version.id}
                      className='flex items-center justify-between text-xs text-gray-600'
                    >
                      <div className='flex items-center space-x-2'>
                        <GitBranch className='w-3 h-3' />
                        <span>{version.version}</span>
                        {version.is_active && (
                          <span className='px-1 py-0.5 bg-green-100 text-green-800 rounded text-xs'>
                            Active
                          </span>
                        )}
                      </div>
                      <span>{formatDate(version.created_at)}</span>
                    </div>
                  ))}

                  {exercise.versions.length > 3 && (
                    <div className='text-xs text-gray-500'>
                      +{exercise.versions.length - 3} more versions
                    </div>
                  )}
                </div>
              )}

              {exercise.versions.length === 0 && (
                <div className='flex items-center space-x-2 text-sm text-gray-500'>
                  <AlertCircle className='w-4 h-4' />
                  <span>No versions synced yet</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </motion.div>

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className='glass-effect rounded-2xl p-6'
      >
        <h3 className='text-lg font-semibold text-gray-900 mb-4'>
          Repository Summary
        </h3>

        <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
          <div className='text-center'>
            <div className='text-2xl font-bold text-hexagon'>
              {exercises.length}
            </div>
            <div className='text-sm text-gray-600'>Total Exercises</div>
          </div>

          <div className='text-center'>
            <div className='text-2xl font-bold text-green-600'>
              {exercises.filter(e => e.versions.length > 0).length}
            </div>
            <div className='text-sm text-gray-600'>Synced Exercises</div>
          </div>

          <div className='text-center'>
            <div className='text-2xl font-bold text-blue-600'>
              {exercises.reduce((sum, e) => sum + e.versions.length, 0)}
            </div>
            <div className='text-sm text-gray-600'>Total Versions</div>
          </div>

          <div className='text-center'>
            <div className='text-2xl font-bold text-purple-600'>
              {exercises.filter(e => e.activeVersion).length}
            </div>
            <div className='text-sm text-gray-600'>Active Versions</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ExerciseVersionManager;
